import { useEffect, useState } from "react";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { useReactiveVar } from "@apollo/client";
import {
  filterStateVar,
  initialFilterStateVar,
} from "@/reactiveVars/organization";
import { FilterContext } from "@/types/FilterContext";
import { filterSidebarContext } from "@/reactiveVars/global";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useUrlState } from "./useUrlSerializer";

interface FilterVariables {
  filterState: FilterState | undefined;
  context: FilterContext | undefined;
  ordering: StudentOrdering | undefined;
  setOrdering: (ordering: StudentOrdering) => void;
}

export interface useFilterSetupParams {
  context?: FilterContext;
  ordering?: StudentOrdering;
  fs?: FilterState;
}

/**
 * useFilterSetup - setup filter variables and based on props and url query params
 *
 * only use at top-level page components
 */
export const useFilterSetup = ({
  context,
  ordering,
  fs,
}: useFilterSetupParams): FilterVariables => {
  const urlFs = useUrlState("FILTER_STATE");
  const urlContext = useUrlState("FILTER_CONTEXT");
  const urlOrdering = useUrlState("STUDENT_ORDERING");

  const [filterContext] = useState<FilterContext | undefined>(
    context || urlContext,
  );

  useEffect(() => {
    filterSidebarContext(filterContext); // let sidebar know the context
  }, [filterContext]);

  const [studentOrdering, setStudentOrdering] = useState<
    StudentOrdering | undefined
  >(() => {
    // override ordering field and/or direction if present in url
    return ordering
      ? ordering.clone({
          direction: urlOrdering?.direction || undefined,
          field: urlOrdering?.field || undefined,
          context: filterContext?.data,
        })
      : urlOrdering;
  });

  const [defaultFs] = useState<FilterState | undefined>(() => {
    return fs ? fs.merge(urlFs) : urlFs;
  });

  const filterState = useReactiveVar(filterStateVar);

  useEffect(() => {
    // url filter state overrides initial filter state
    initialFilterStateVar(defaultFs); // sets state of filter sidebar form
    filterStateVar(defaultFs); // sets state of actual filter being used in query
  }, [urlFs, urlContext, urlOrdering, defaultFs]);

  // return these as a group to prevent uneceesary canceled graphql calls
  if (filterState && filterContext && studentOrdering) {
    return {
      filterState: filterState,
      context: filterContext,
      ordering: studentOrdering,
      setOrdering: setStudentOrdering,
    };
  }

  return {
    filterState: undefined,
    context: undefined,
    ordering: undefined,
    setOrdering: setStudentOrdering,
  };
};
